import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ConfigurationAutomationProtocolsModal
    from '@/modules/configurationModule/detail/automation/protocols/components/modal/configuration-automation-protocols-modal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import {
    ConfigurationAutomationProtocolsGridState,
    initialConfigurationAutomationProtocolsGridState
} from '@/modules/configurationModule/detail/automation/protocols/components/grid/configuration-automation-protocols-grid.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { ConfigurationAutomaticOntologyProtocolResponse } from '@/modules/configurationModule/detail/automation/protocols/services/configuration-automatic-ontology-protocol/configuration-automatic-ontology-protocol.dto'
import { ConfigurationOntologyForAutomaticProtocolResponse } from '@/modules/configurationModule/detail/automation/protocols/services/configuration-ontology-for-automatic-protocol/configuration-ontology-for-automatic-protocol.dto'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationAutomationProtocolsModal
    }
})
export default class ConfigurationAutomationProtocolsGrid extends BaseCrudServerSideComponent<ConfigurationOntologyForAutomaticProtocolResponse> implements BaseCrud {
    @Ref() readonly automationProtocolsModalWrapper!: typeof ModalWrapper

    state: ConfigurationAutomationProtocolsGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationAutomationProtocolsGridState()
    }

    /// SERVICES CALLS ///

    private async _deleteAutomaticOntologyProtocols(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteAutomaticOntologyProtocols(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(automaticOntologyProtocol: ConfigurationAutomaticOntologyProtocolResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (automaticOntologyProtocol) {
            this.showModal(this.$tc('configuration.actions.update'), automaticOntologyProtocol, this.automationProtocolsModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(automaticOntologyProtocol => automaticOntologyProtocol.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.automationProtocolsModalWrapper)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            }
        ]
    }
}
